/**
 * La única petición a internet de DuoFit (Fase 6, 0.13.0; Req. 2.9).
 *
 * Busca un producto por su código de barras en Open Food Facts. Lo que sale
 * del teléfono es **solo el código de barras**: ni comidas, ni pesajes, ni
 * medidas, ni historial. Nada se guarda al consultar; el resultado se revisa
 * y se guarda aparte.
 *
 * El resto de la app sigue funcionando sin conexión: si no hay red, esto lo
 * dice y no rompe nada.
 */

import { CAMPOS_OFF, leerProducto, type LecturaDeProducto, type ProductoOff } from '@/domain/codigo-barras';

const BASE = 'https://world.openfoodfacts.org/api/v2/product';
/** Si tarda más, se corta: en el súper la señal puede estar mal. */
const LIMITE_MS = 12_000;

export type Busqueda =
  | { tipo: 'ok'; lectura: LecturaDeProducto }
  /** El teléfono dice que no hay red, o la petición no salió. */
  | { tipo: 'sin_conexion' }
  /** Tardó más de lo razonable. */
  | { tipo: 'tardo' }
  /** Open Food Facts respondió con un error o algo que no se entiende. */
  | { tipo: 'error'; detalle: string };

export const TEXTO_BUSQUEDA: Readonly<Record<'sin_conexion' | 'tardo', string>> = {
  sin_conexion:
    'Buscar un producto necesita internet y ahora no hay. Todo lo demás de DuoFit sigue funcionando: puedes capturar el alimento a mano desde su etiqueta.',
  tardo: 'Open Food Facts no respondió a tiempo. Puedes intentar otra vez o capturar el alimento a mano.',
};

/**
 * Pide el producto. `traer` se puede sustituir en las pruebas; en la app es
 * `fetch`. Solo pide los campos que se usan, para no bajar la ficha completa.
 */
export async function buscarPorCodigo(
  codigo: string,
  traer: typeof fetch = fetch,
  enLinea: () => boolean = () => navigator.onLine,
): Promise<Busqueda> {
  if (!enLinea()) return { tipo: 'sin_conexion' };

  const control = new AbortController();
  const reloj = setTimeout(() => control.abort(), LIMITE_MS);
  try {
    const url = `${BASE}/${encodeURIComponent(codigo)}.json?fields=${encodeURIComponent(CAMPOS_OFF)}`;
    const respuesta = await traer(url, { signal: control.signal, headers: { Accept: 'application/json' } });
    // 404 es una respuesta válida de la API: ese código no está en la base
    if (respuesta.status === 404) return { tipo: 'ok', lectura: { tipo: 'sin_producto' } };
    if (!respuesta.ok) return { tipo: 'error', detalle: `Open Food Facts respondió ${respuesta.status}.` };
    const cuerpo = (await respuesta.json()) as ProductoOff;
    return { tipo: 'ok', lectura: leerProducto(cuerpo, codigo) };
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return { tipo: 'tardo' };
    // Sin red, fetch falla con TypeError antes de llegar a ningún servidor
    return { tipo: 'sin_conexion' };
  } finally {
    clearTimeout(reloj);
  }
}
